import { properties } from "@repoflow/common/schema-fields";
import { KINDS } from "./kind";
import { VISIBILITIES, PRIVATE } from "./visibility";

const { createdAt, updatedAt } = properties;

export { createdAt, updatedAt };

// `${orgId}/${siteId}/${kind}`, and the table's partition.
//
// WHY THE KIND IS IN THE KEY
//
// Pages and sections partition on `${orgId}/${siteId}` because a site's pages
// are one listing. A site's files are TWO listings that never mix — Images and
// Documents are separate screens, reached by separate tabs, and nothing renders
// them together. Putting the kind in the partition makes each tab a plain
// `Query` against one partition with no filter, which is the only listing shape
// `@repoflow/aws/datorm` has: `DynamoDB.list` accepts `last`, `limit`,
// `orderBy` and `orderMode` and there is no FilterExpression anywhere in the
// repo (docs/search.md §1).
//
// It also bounds full-text search to one site's files of one kind, because a
// search shard IS a partition. That matters more than it looks: the shard
// backend searches with `fuzzy: 0.2`, so a query that had to carry a siteId as
// a term would match a NEIGHBOURING site's id at edit distance 2 —
// `e2e-1755820000000` against `e2e-1755820000001`. Site scoping could not have
// been a search term; it had to be the partition.
export const siteFileKey = {
  type: "string",
};

export const siteFileKeyOf = (orgId, siteId, kind) =>
  `${orgId}/${siteId}/${kind}`;

// The bucketws file id — a uuid4 minted by bucketws' model, not by this
// service. It is the range key here for the same reason it is there: it is the
// identity of the bytes, and this row is an index entry pointing at them.
export const fileId = {
  type: "string",
};

// The bucketws BUCKET the bytes are in. Stored per row rather than looked up
// from organization-buckets-table on every read, so serving a file costs one
// DynamoDB read instead of two — and so a row stays resolvable if an
// organization's mapping is ever re-provisioned.
export const bucketId = {
  type: "string",
};

export const orgId = {
  type: "string",
};

export const siteId = {
  type: "string",
};

export const kind = {
  type: "string",
  enum: KINDS,
};

// The original filename as the browser reported it, sans extension. bucketws
// stores none — its object key is `<fileId>/file.<ext>` and the human name is
// lost the moment the form is posted — so if a person is ever to see the file
// they uploaded called what they called it, this service has to keep it.
export const name = {
  label: "Name",
  type: "string",
  default: "",
};

// Whether the bytes may be served to somebody with no credential — see
// @common/schema/site-file/visibility for what that gates and what it does not.
//
// `private` is the default HERE as well as in the enum's own module, and the
// two are not redundant. This one is what datorm writes onto a row that has no
// value, and it is what the edit form holds before anybody touches the control;
// a select shows its first option whatever the form is carrying, so the first
// option and the default have to be the same string or an untouched form looks
// like it is saying something it is not.
export const visibility = {
  label: "Visibility",
  type: "string",
  widget: "select",
  enum: VISIBILITIES,
  default: PRIVATE,
};

export const hash = {
  type: "string",
};

export const ext = {
  label: "Extension",
  type: "string",
};

// Same constraint bucketws applies to the value it accepts, applied here for
// the same reason: `ext` is concatenated into a URL and selects which bucket
// policy the upload runs under.
//
// Deliberately NOT applied to `ext` itself. The data schema is revalidated on
// every write, so tightening it there would make a row written before this
// existed un-updatable — the file could never be renamed, retagged or removed.
// bucketws' schema carries the identical note against its own `extInput`.
export const extInput = {
  ...ext,
  pattern: "^[a-z0-9]{1,10}$",
};

// A SNAPSHOT of bucketws' state, not the truth.
//
// bucketws owns `size`, `status` and `metadata`: it writes them from its
// upload-handler, out of band, after the browser has posted the bytes to S3. A
// row here is created before any of that has happened and therefore starts at
// `processing`/0, exactly as bucketws' own row does.
//
// The snapshot exists so a listing costs one DynamoDB Query. Resolving these
// live would be one cross-service GraphQL round trip PER ROW per page of the
// list, which is the shape that made the archived project's file list slow.
// `model/site-file.sync` is what refreshes them, and the details screen calls
// it on open — so a row that was still `processing` when its upload finished
// heals the first time anyone looks at it.
export const size = {
  label: "Size",
  type: "integer",
  default: 0,
};

export const status = {
  type: "string",
};

export const metadata = {
  type: "object",
  default: {},
};

// Owned HERE, and mirrored to bucketws on write.
//
// They are stored locally because they are what the listing shows and what its
// search shard indexes, and neither can read across the wire. They are mirrored
// because a file that is self-describing only inside pagews is opaque to anyone
// looking at the bucket in bucketws' own UI. `model/site-file.update` writes
// both and treats the bucketws leg as best-effort — see the note there.
export const description = {
  label: "Description",
  type: "string",
  default: "",
};

export const tags = {
  label: "Tags",
  type: "array",
  items: {
    type: "string",
    default: "",
  },
  default: [],
  widget: "tags",
};
